import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore, useUploadStore, useProcessingStagesStore } from '../../store';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const STAGE_LABELS: Record<string, string> = {
  COMPLIANCE_CHECK: 'Scanning for violations',
  SCORING: 'Calculating score',
};

const GlobalUploadIndicator: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { uploads, setStageLabel, updateVideoStatus, dismiss } = useUploadStore();
  const { setProcessingStage, clearProcessingStage } = useProcessingStagesStore();
  const [collapsed, setCollapsed] = useState(false);

  const hasUploading = uploads.some((u) => u.status === 'uploading');
  const hasProcessing = uploads.some((u) => u.status === 'processing');
  const activeCount = uploads.filter((u) => u.status === 'uploading' || u.status === 'processing').length;

  /* Warn before tab close while a file is still transferring */
  useEffect(() => {
    if (!hasUploading) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUploading]);

  /* SSE — only open while at least one video is in the processing pipeline */
  useEffect(() => {
    if (!hasProcessing) return;
    const { accessToken } = useAuthStore.getState();
    if (!accessToken) return;

    const es = new EventSource(`${API_URL}/videos/events?token=${encodeURIComponent(accessToken)}`);

    es.onmessage = (event) => {
      try {
        const data: { videoId: string; status: string; stage?: string; title?: string } = JSON.parse(event.data);

        if (data.status === 'PROCESSING' && data.stage) {
          const label = STAGE_LABELS[data.stage] ?? data.stage;
          setStageLabel(data.videoId, label);
          setProcessingStage(data.videoId, label);
          return;
        }

        if (data.status === 'SCORED' || data.status === 'FLAGGED' || data.status === 'FAILED') {
          const normalized = data.status.toLowerCase() as 'scored' | 'flagged' | 'failed';
          const tracked = useUploadStore.getState().uploads.find((u) => u.videoId === data.videoId);
          const title = tracked?.title ?? 'Video';
          updateVideoStatus(data.videoId, normalized);
          clearProcessingStage(data.videoId);
          if (data.status === 'SCORED') toast.success(`"${title}" scored successfully!`);
          else if (data.status === 'FLAGGED') toast.error(`"${title}" — hard blocks detected.`);
          else toast.error(`"${title}" processing failed.`);
        }
      } catch {
        // ignore malformed events
      }
    };

    es.onerror = () => { if (es.readyState === EventSource.CLOSED) es.close(); };
    return () => es.close();
  }, [hasProcessing, setStageLabel, updateVideoStatus, setProcessingStage, clearProcessingStage]);

  if (!isAuthenticated || uploads.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 shadow-2xl">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 h-11 bg-slate-900 dark:bg-slate-800 border border-slate-700 dark:border-slate-700 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2.5">
          {activeCount > 0 ? (
            <span className="material-symbols-outlined text-[16px] text-primary animate-spin">sync</span>
          ) : (
            <span className="material-symbols-outlined text-[16px] text-emerald-400">check_circle</span>
          )}
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-200">
            {activeCount > 0 ? `${activeCount} upload${activeCount > 1 ? 's' : ''} in progress` : 'Uploads complete'}
          </span>
        </div>
        <span className="material-symbols-outlined text-[16px] text-slate-400">
          {collapsed ? 'expand_less' : 'expand_more'}
        </span>
      </div>

      {/* Upload list */}
      {!collapsed && (
        <div className="bg-white dark:bg-slate-900 border-x border-b border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto">
          {uploads.map((upload) => {
            const isTerminal = upload.status === 'scored' || upload.status === 'flagged' || upload.status === 'failed';

            const statusDot: Record<typeof upload.status, string> = {
              uploading: 'bg-primary animate-pulse',
              processing: 'bg-amber-400 animate-pulse',
              scored: 'bg-emerald-500',
              flagged: 'bg-red-500',
              failed: 'bg-slate-400',
            };

            const statusBadge: Record<typeof upload.status, string> = {
              uploading: 'text-primary border-primary/30',
              processing: 'text-amber-500 border-amber-300/40',
              scored: 'text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/40',
              flagged: 'text-red-500 border-red-200 dark:border-red-900/40',
              failed: 'text-slate-500 border-slate-200 dark:border-slate-700',
            };

            const statusText: Record<typeof upload.status, string> = {
              uploading: `${upload.progress}%`,
              processing: upload.stageLabel || 'Processing...',
              scored: 'Scored',
              flagged: 'Flagged',
              failed: 'Failed',
            };

            const isNavigable = (upload.status === 'scored' || upload.status === 'flagged') && upload.videoId != null;

            const handleRowClick = () => {
              if (!isNavigable) return;
              dismiss(upload.clientId);
              navigate(`/videos/${upload.videoId}`);
            };

            return (
              <div
                key={upload.clientId}
                onClick={handleRowClick}
                className={`px-4 py-3 space-y-2 transition-colors ${isNavigable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60' : ''}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot[upload.status]}`} />
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate flex-1">
                    {upload.title}
                  </span>
                  {isNavigable && (
                    <span className="material-symbols-outlined text-[14px] text-slate-400 dark:text-slate-500 shrink-0">open_in_new</span>
                  )}
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 border bg-transparent shrink-0 ${statusBadge[upload.status]}`}>
                    {statusText[upload.status]}
                  </span>
                  {isTerminal && (
                    <button
                      onClick={(e) => { e.stopPropagation(); dismiss(upload.clientId); }}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors ml-1 shrink-0 cursor-pointer"
                      aria-label="Dismiss"
                    >
                      <span className="material-symbols-outlined text-[14px]">close</span>
                    </button>
                  )}
                </div>

                {/* Progress bar — only during upload */}
                {upload.status === 'uploading' && (
                  <div className="h-0.5 bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GlobalUploadIndicator;
